/**
 * ShipmentStatusPlanningTab — Sprint RUN 1
 *
 * Toont statusplanning voor één shipment via de bestaande canonical
 * backend services (`admin_shipment_ops_router.py`). Geen tweede status-engine,
 * geen frontend-only statussen.
 *
 * UI-onderdelen:
 *  - Huidige status + transport-mode + template
 *  - ETD / ETA (read-only voor Medewerker; editable met permission)
 *  - Timeline van scheduled + executed + failed/skipped events
 *  - Apply-template dialog
 *  - Event cancel/retry acties
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Clock, CheckCircle2, XCircle, AlertTriangle, Loader2,
  PlayCircle, Ban, Bell, BellOff, RefreshCw, ClipboardList,
} from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const STATE_STYLES = {
  scheduled:  { color: "text-slate-700", bg: "bg-slate-100 border-slate-200", icon: Clock,        label: "GEPLAND" },
  executed:   { color: "text-emerald-800", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle2, label: "UITGEVOERD" },
  processing: { color: "text-amber-800", bg: "bg-amber-50 border-amber-200", icon: Loader2,     label: "BEZIG" },
  failed:     { color: "text-red-800", bg: "bg-red-50 border-red-200", icon: XCircle,      label: "MISLUKT" },
  cancelled:  { color: "text-slate-500", bg: "bg-slate-50 border-slate-200", icon: Ban,          label: "GEANNULEERD" },
  skipped:    { color: "text-slate-500", bg: "bg-slate-50 border-slate-200", icon: AlertTriangle, label: "OVERGESLAGEN" },
};

function StateIcon({ state, className = "w-4 h-4" }) {
  const meta = STATE_STYLES[state] || STATE_STYLES.scheduled;
  const Icon = meta.icon;
  return <Icon className={className} />;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ShipmentStatusPlanningTab({ shipmentId, label, onReload }) {
  const { hasPermission } = useAuth();
  // Sprint RUN 1 — canEdit gate mirrort ConsolidationStatusPlanningTab: alleen
  // Manager/Admin/Owner (of custom roles met tracking:manage) mogen templates
  // toepassen of scheduled events annuleren. Backend `require_perm` handhaaft
  // dit hoe dan ook — dit is puur UI hygiene.
  const canEdit = hasPermission("tracking:manage");

  const [schedule, setSchedule] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedTpl, setSelectedTpl] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [sched, tpls, hist] = await Promise.all([
        adminApi.get(`/admin/shipments/${shipmentId}/schedule`),
        adminApi.get(`/admin/status-flow-templates`),
        adminApi.get(`/admin/shipments/${shipmentId}/status-history?limit=50`),
      ]);
      setSchedule(sched?.data || null);
      setTemplates(tpls?.data?.items || tpls?.data || []);
      setHistory(hist?.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Kon planning niet laden."));
    } finally {
      setBusy(false);
    }
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  const events = schedule?.items || [];
  // Combine schedule events + status history (executed status changes may
  // originate from consolidation propagation or manual actions without a
  // corresponding scheduled event).
  const timelineRows = useMemo(() => {
    const rows = [];
    for (const ev of events) {
      rows.push({
        kind: "scheduled",
        state: ev.state || "scheduled",
        status: ev.new_status || ev.status,
        when: ev.executed_at || ev.scheduled_for,
        planned: ev.scheduled_for,
        source: ev.rule_type,
        notify: ev.notify_customer,
        error: ev.error_message,
        id: ev.id,
      });
    }
    for (const h of history) {
      // Only add executed transitions from history not backed by a scheduled event
      const alreadyInSchedule = events.some(
        (e) => e.state === "executed" && (e.new_status === h.new_status) &&
               (e.executed_at || "").slice(0, 16) === (h.created_at || "").slice(0, 16)
      );
      if (!alreadyInSchedule && h.state === "applied") {
        rows.push({
          kind: "history",
          state: "executed",
          status: h.new_status,
          when: h.created_at,
          planned: null,
          source: h.source,
          notify: null,
          id: `h-${h.id || h.created_at}`,
        });
      }
    }
    rows.sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0));
    return rows;
  }, [events, history]);

  const currentStatus = label?.operational_status || label?.status || "—";
  const template = schedule?.template_code || label?.status_flow_template || null;
  const etd = schedule?.etd || label?.etd;
  const eta = schedule?.eta || label?.eta;
  const transportMode = (label?.transport_mode || label?.service_type || "").toUpperCase();

  const submitApply = async () => {
    if (!selectedTpl) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/shipments/${shipmentId}/apply-template`, {
        template_code: selectedTpl, dry_run: false,
      });
      toast.success("Template toegepast.");
      setApplyOpen(false);
      await load();
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiError(e, "Template toepassen mislukt."));
    } finally {
      setBusy(false);
    }
  };

  const cancelFuture = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/shipments/${shipmentId}/schedule/cancel-future`, {});
      toast.success("Toekomstige events geannuleerd.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Annuleren mislukt."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="shipment-planning-tab">
      {/* Header stat-strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <Stat label="Huidige status" value={currentStatus} testId="planning-current-status" />
        <Stat label="Transport"      value={transportMode || "—"} />
        <Stat label="Template"       value={template || "—"} testId="planning-template" />
        <Stat label="ETD"            value={fmtDateTime(etd)} />
        <Stat label="ETA"            value={fmtDateTime(eta)} />
      </div>

      {/* Action bar */}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="rounded-none text-xs bg-[#082c50] hover:bg-[#0a3b6a] text-white"
            onClick={() => { setSelectedTpl(""); setApplyOpen(true); }}
            data-testid="planning-apply-template-btn"
          >
            <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Statusflow-template toepassen
          </Button>
          <Button
            size="sm" variant="outline"
            className="rounded-none text-xs"
            onClick={cancelFuture}
            disabled={busy || events.filter((e) => e.state === "scheduled").length === 0}
            data-testid="planning-cancel-future-btn"
          >
            <Ban className="w-3.5 h-3.5 mr-1.5" /> Toekomst annuleren
          </Button>
          <Button
            size="sm" variant="ghost"
            className="rounded-none text-xs"
            onClick={load}
            disabled={busy}
            data-testid="planning-reload-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Herladen
          </Button>
        </div>
      )}

      {/* Timeline */}
      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-700">
          Tijdlijn ({timelineRows.length})
        </div>
        {timelineRows.length === 0 && !busy && (
          <div className="p-6 text-sm text-slate-500 text-center italic">
            Geen geplande of uitgevoerde events. Pas een statusflow-template toe om te starten.
          </div>
        )}
        <ol className="divide-y divide-slate-100" data-testid="planning-timeline">
          {timelineRows.map((row, idx) => {
            const meta = STATE_STYLES[row.state] || STATE_STYLES.scheduled;
            return (
              <li key={row.id || idx}
                  className={`flex items-start gap-3 px-4 py-3 ${meta.bg} border-l-4`}
                  data-testid={`planning-event-${row.state}`}>
                <div className={`mt-0.5 ${meta.color}`}>
                  <StateIcon state={row.state} className={`w-5 h-5 ${row.state === "processing" ? "animate-spin" : ""}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-bold text-slate-900">
                      {row.status || "—"}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${meta.color} bg-white`}>
                      {meta.label}
                    </span>
                    {row.notify === true && (
                      <span className="text-[10px] text-emerald-700 flex items-center gap-0.5">
                        <Bell className="w-3 h-3" /> notificatie
                      </span>
                    )}
                    {row.notify === false && (
                      <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                        <BellOff className="w-3 h-3" /> geen mail
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600 flex flex-wrap gap-3">
                    {row.planned && (
                      <span><Calendar className="inline w-3 h-3 mr-1 -mt-0.5" />
                        gepland {fmtDateTime(row.planned)}</span>
                    )}
                    {row.when && row.state === "executed" && (
                      <span><CheckCircle2 className="inline w-3 h-3 mr-1 -mt-0.5" />
                        uitgevoerd {fmtDateTime(row.when)}</span>
                    )}
                    {row.source && (
                      <span className="text-slate-500">bron: {row.source}</span>
                    )}
                  </div>
                  {row.error && (
                    <div className="mt-1 text-[11px] text-red-700 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5" /> {row.error}
                    </div>
                  )}
                </div>
                {row.kind === "scheduled" && row.state === "failed" && canEdit && (
                  <Button
                    size="sm" variant="ghost"
                    className="rounded-none text-[10px] text-amber-700 hover:bg-amber-50"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await adminApi.post(
                          `/admin/tracking-automation/events/${row.id}/retry`, {},
                        );
                        toast.success("Event opnieuw gepland.");
                        await load();
                      } catch (e) {
                        toast.error(formatApiError(e, "Retry mislukt."));
                      } finally {
                        setBusy(false);
                      }
                    }}
                    data-testid={`planning-event-retry-${row.id}`}
                  >
                    <PlayCircle className="w-3 h-3 mr-1" /> Opnieuw proberen
                  </Button>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Apply template dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent data-testid="planning-apply-dialog">
          <DialogHeader>
            <DialogTitle>Statusflow-template toepassen</DialogTitle>
            <DialogDescription>
              Kies een canonical template. Bestaande scheduled events die nog niet
              zijn uitgevoerd worden opnieuw berekend.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Select value={selectedTpl} onValueChange={setSelectedTpl}>
              <SelectTrigger className="rounded-none" data-testid="planning-tpl-select">
                <SelectValue placeholder="Kies template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.code || t.template_code} value={t.code || t.template_code}>
                    {(t.code || t.template_code)} — {t.name || t.title || t.description || ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} className="rounded-none text-xs">
              Annuleren
            </Button>
            <Button
              onClick={submitApply}
              disabled={busy || !selectedTpl}
              className="rounded-none text-xs bg-[#082c50] hover:bg-[#0a3b6a] text-white"
              data-testid="planning-apply-confirm"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Toepassen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="border border-slate-200 bg-slate-50 px-3 py-2" data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-xs font-bold text-slate-900 truncate">
        {value}
      </div>
    </div>
  );
}
